/**
 * LoadingProgress Component
 * T118: Percentage bar for showing loading progress
 */

import { cn } from '@/lib/utils';

export interface LoadingProgressProps {
  /** Progress value (0-100) */
  progress: number;
  /** Optional label to display */
  label?: string;
  /** Additional class names */
  className?: string;
}

/**
 * Loading progress bar component
 */
export function LoadingProgress({
  progress,
  label,
  className,
}: LoadingProgressProps) {
  // Clamp and round progress
  const clampedProgress = Math.max(0, Math.min(100, Math.round(progress)));

  return (
    <div className={cn('space-y-1', className)}>
      {label && (
        <p className="text-sm text-[var(--color-charcoal)]/70">{label}</p>
      )}
      <div
        role="progressbar"
        aria-valuenow={clampedProgress}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Progress: ${clampedProgress}%`}
        className="h-2 bg-[var(--glass-bg)] rounded-full overflow-hidden"
      >
        <div
          className="h-full gradient-bg transition-all duration-300 ease-out rounded-full"
          style={{ width: `${clampedProgress}%` }}
        />
      </div>
      <span className="text-xs font-medium text-[var(--color-charcoal)]/70 block text-center">
        {clampedProgress}%
      </span>
    </div>
  );
}

export default LoadingProgress;
